/**
 * @fileoverview Modal component for creating and editing Buzzer Battle Question Sets ("De barrio en barrio").
 * Allows operators to configure question sets with dynamic questions, Drag & Drop question images,
 * dynamic option lists (A, B, C, D, ...), and single correct answer radio selections.
 * 
 * @module components/Admin/Games/BuzzerBattle/BuzzerBattleQuestionModal
 */

import React, { useState, useEffect } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from './../../../Providers/FloatMessage';
import { UploadMedia, SaveQuestionSet } from './../../../../utilities/BL/BuzzerBattle';

/**
 * Option letter prefixes ($A, B, C, D, E, F, G, H$).
 */
const OPTION_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

/**
 * Creates a default question object template.
 * 
 * @function createEmptyQuestion
 * @param {number} orderIndex - Zero-based order index.
 * @returns {Object} Fresh question object.
 */
const createEmptyQuestion = (orderIndex = 0) => ({
    id: 0,
    instruction: '',
    imageUrl: '',
    orderIndex: orderIndex,
    options: [
        { id: 0, text: '', isCorrect: true, orderIndex: 0 },
        { id: 0, text: '', isCorrect: false, orderIndex: 1 },
        { id: 0, text: '', isCorrect: false, orderIndex: 2 },
        { id: 0, text: '', isCorrect: false, orderIndex: 3 }
    ]
});

/**
 * Reusable Drag & Drop Field component for question image upload.
 * 
 * @function QuestionImageDropzone
 * @param {Object} props - Component properties.
 * @param {string} props.currentValue - Current image URL.
 * @param {number} props.questionIndex - Index of the target question.
 * @param {Function} props.onUploadFile - Upload trigger function.
 * @param {Function} props.onClearImage - Clear image trigger function.
 * @param {Function} props.resolveMediaUrl - Media URL resolver.
 * @param {boolean} props.isUploading - Indicator if upload is active.
 */
const QuestionImageDropzone = ({
    currentValue,
    questionIndex,
    onUploadFile,
    onClearImage,
    resolveMediaUrl,
    isUploading
}) => {
    const [isDragHoverActive, setIsDragHoverActive] = useState(false);

    const handleDragOver = (dragEvent) => {
        dragEvent.preventDefault();
        setIsDragHoverActive(true);
    };

    const handleDragLeave = () => {
        setIsDragHoverActive(false);
    };

    const handleDrop = (dropEvent) => {
        dropEvent.preventDefault();
        setIsDragHoverActive(false);
        const selectedFile = dropEvent.dataTransfer.files[0];
        if (selectedFile) {
            onUploadFile(selectedFile, questionIndex);
        }
    };

    const handleNativeFileChange = (changeEvent) => {
        const selectedFile = changeEvent.target.files[0];
        if (selectedFile) {
            onUploadFile(selectedFile, questionIndex);
        }
    };

    return (
        <div className="mb-2">
            <label className="form-label fw-bold small text-muted mb-1">
                <i className="bi bi-image me-1"></i> Imagen de la Pregunta (Opcional):
            </label>
            <div
                className={`border border-2 border-dashed rounded-3 p-2 text-center cursor-pointer transition ${
                    isDragHoverActive ? 'border-primary bg-primary-subtle' : 'border-secondary-subtle bg-light'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => document.getElementById(`question-file-input-${questionIndex}`).click()}
                style={{ minHeight: '65px', cursor: 'pointer' }}
            >
                {isUploading ? (
                    <div className="py-1">
                        <div className="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
                        <span className="small text-primary fw-bold">Subiendo imagen...</span>
                    </div>
                ) : (
                    <div className="py-1">
                        <i className="bi bi-cloud-arrow-up text-primary fs-5 d-inline me-2"></i>
                        <span className="small text-muted">
                            Arrastra aquí la imagen o haz <strong className="text-primary">clic para buscar</strong>
                        </span>
                    </div>
                )}
                <input
                    id={`question-file-input-${questionIndex}`}
                    type="file"
                    className="d-none"
                    accept="image/*"
                    onChange={handleNativeFileChange}
                />
            </div>
            {currentValue && (
                <div className="mt-2 text-center bg-white p-2 rounded-3 border shadow-sm position-relative d-inline-block">
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-danger position-absolute top-0 end-0 m-1 rounded-circle p-1"
                        onClick={(e) => {
                            e.stopPropagation();
                            onClearImage(questionIndex);
                        }}
                        title="Eliminar imagen"
                        style={{ width: '22px', height: '22px', lineHeight: '10px' }}
                    >
                        <i className="bi bi-x"></i>
                    </button>
                    <img
                        src={resolveMediaUrl(currentValue)}
                        alt="Preview"
                        style={{ maxHeight: '80px', maxWidth: '100%', objectFit: 'contain' }}
                        onError={(e) => {
                            e.target.style.display = 'none';
                        }}
                    />
                </div>
            )}
        </div>
    );
};

/**
 * Buzzer Battle Question Modal Component.
 * 
 * @function BuzzerBattleQuestionModal
 * @param {Object} props - Component properties.
 * @param {boolean} props.isOpen - Controls modal visibility.
 * @param {Function} props.onCloseModal - Handler to close modal.
 * @param {Object} [props.initialQuestionSetData=null] - Existing question set to edit, or null.
 * @param {Function} props.onQuestionSetSavedSuccessfully - Handler triggered after successful save.
 * @returns {React.ReactElement|null} Modal markup.
 */
export const BuzzerBattleQuestionModal = ({
    isOpen,
    onCloseModal,
    initialQuestionSetData = null,
    onQuestionSetSavedSuccessfully
}) => {
    const { showMessage } = useFloatMessage();
    const [isSavingInProgress, setIsSavingInProgress] = useState(false);
    const [uploadingQuestionIndex, setUploadingQuestionIndex] = useState(null);

    // Initial Question Set Template
    const defaultQuestionSetStructure = {
        id: 0,
        title: '',
        description: '',
        questions: [createEmptyQuestion(0)]
    };

    const [questionSetFormState, setQuestionSetFormState] = useState(defaultQuestionSetStructure);

    useEffect(() => {
        if (initialQuestionSetData && (initialQuestionSetData.id > 0 || initialQuestionSetData.title)) {
            const sanitizedQuestions = Array.isArray(initialQuestionSetData.questions) && initialQuestionSetData.questions.length > 0
                ? initialQuestionSetData.questions.map((q, qIndex) => ({
                    id: q.id || 0,
                    instruction: q.instruction || '',
                    imageUrl: q.imageUrl || '',
                    orderIndex: qIndex,
                    options: Array.isArray(q.options) && q.options.length >= 2
                        ? q.options.map((opt, optIndex) => ({
                            id: opt.id || 0,
                            text: opt.text || '',
                            isCorrect: opt.isCorrect === true || opt.isCorrect === 1 || opt.isCorrect === 'true',
                            orderIndex: optIndex
                        }))
                        : [
                            { id: 0, text: '', isCorrect: true, orderIndex: 0 },
                            { id: 0, text: '', isCorrect: false, orderIndex: 1 }
                        ]
                }))
                : [createEmptyQuestion(0)];

            setQuestionSetFormState({
                id: initialQuestionSetData.id || 0,
                title: initialQuestionSetData.title || '',
                description: initialQuestionSetData.description || '',
                questions: sanitizedQuestions
            });
        } else {
            setQuestionSetFormState(defaultQuestionSetStructure);
        }
    }, [initialQuestionSetData, isOpen]);

    if (!isOpen) return null;

    /**
     * Resolves media paths into absolute URLs for thumbnail preview.
     */
    const resolveMediaUrl = (mediaPath) => {
        if (!mediaPath) return '';
        if (mediaPath.startsWith('http://') || mediaPath.startsWith('https://') || mediaPath.startsWith('blob:')) {
            return mediaPath;
        }
        const apiBaseUrl = (process.env.REACT_APP_API_URL || '').replace('/api/', '/').replace('/api', '/');
        return `${apiBaseUrl}${mediaPath.startsWith('/') ? mediaPath.substring(1) : mediaPath}`;
    };

    /**
     * Updates top-level title or description.
     */
    const handleHeaderFieldChange = (fieldName, fieldValue) => {
        setQuestionSetFormState((previousState) => ({
            ...previousState,
            [fieldName]: fieldValue
        }));
    };

    // =========================================================================
    // QUESTION OPERATIONS
    // =========================================================================

    const handleAddQuestion = () => {
        setQuestionSetFormState((previousState) => ({
            ...previousState,
            questions: [
                ...previousState.questions,
                createEmptyQuestion(previousState.questions.length)
            ]
        }));
    };

    const handleRemoveQuestion = (questionIndexToRemove) => {
        if (questionSetFormState.questions.length <= 1) {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: 'El banco debe contener al menos 1 pregunta.' });
            return;
        }
        setQuestionSetFormState((previousState) => ({
            ...previousState,
            questions: previousState.questions
                .filter((_, index) => index !== questionIndexToRemove)
                .map((q, idx) => ({ ...q, orderIndex: idx }))
        }));
    };

    const handleMoveQuestion = (questionIndex, direction) => {
        const targetIndex = direction === 'up' ? questionIndex - 1 : questionIndex + 1;
        if (targetIndex < 0 || targetIndex >= questionSetFormState.questions.length) return;

        setQuestionSetFormState((previousState) => {
            const reordered = [...previousState.questions];
            const temp = reordered[questionIndex];
            reordered[questionIndex] = reordered[targetIndex];
            reordered[targetIndex] = temp;
            return {
                ...previousState,
                questions: reordered.map((q, idx) => ({ ...q, orderIndex: idx }))
            };
        });
    };

    const handleQuestionInstructionChange = (questionIndex, instructionText) => {
        setQuestionSetFormState((previousState) => {
            const updatedQuestions = [...previousState.questions];
            updatedQuestions[questionIndex] = {
                ...updatedQuestions[questionIndex],
                instruction: instructionText
            };
            return {
                ...previousState,
                questions: updatedQuestions
            };
        });
    };

    const handleQuestionImageUpload = async (fileObject, questionIndex) => {
        setUploadingQuestionIndex(questionIndex);
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Subiendo imagen de la pregunta...' });

        try {
            const uploadResponse = await UploadMedia(fileObject, 'question');
            if (uploadResponse.success && uploadResponse.url) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Imagen cargada correctamente.' });
                setQuestionSetFormState((previousState) => {
                    const updatedQuestions = [...previousState.questions];
                    updatedQuestions[questionIndex] = {
                        ...updatedQuestions[questionIndex],
                        imageUrl: uploadResponse.url
                    };
                    return {
                        ...previousState,
                        questions: updatedQuestions
                    };
                });
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: uploadResponse.message || 'Error al subir la imagen de la pregunta.'
                });
            }
        } catch (uploadError) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error inesperado al subir la imagen.' });
        } finally {
            setUploadingQuestionIndex(null);
        }
    };

    const handleClearQuestionImage = (questionIndex) => {
        setQuestionSetFormState((previousState) => {
            const updatedQuestions = [...previousState.questions];
            updatedQuestions[questionIndex] = {
                ...updatedQuestions[questionIndex],
                imageUrl: ''
            };
            return {
                ...previousState,
                questions: updatedQuestions
            };
        });
    };

    // =========================================================================
    // OPTIONS OPERATIONS
    // =========================================================================

    const handleAddOptionToQuestion = (questionIndex) => {
        const targetQuestion = questionSetFormState.questions[questionIndex];
        if (!targetQuestion) return;

        if (targetQuestion.options.length >= 8) {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Se admite un máximo de 8 opciones por pregunta.' });
            return;
        }

        setQuestionSetFormState((previousState) => {
            const updatedQuestions = [...previousState.questions];
            const updatedOptions = [
                ...updatedQuestions[questionIndex].options,
                {
                    id: 0,
                    text: '',
                    isCorrect: false,
                    orderIndex: updatedQuestions[questionIndex].options.length
                }
            ];
            updatedQuestions[questionIndex] = {
                ...updatedQuestions[questionIndex],
                options: updatedOptions
            };
            return {
                ...previousState,
                questions: updatedQuestions
            };
        });
    };

    const handleRemoveOptionFromQuestion = (questionIndex, optionIndexToRemove) => {
        const targetQuestion = questionSetFormState.questions[questionIndex];
        if (!targetQuestion) return;

        if (targetQuestion.options.length <= 2) {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Cada pregunta debe tener al menos 2 opciones.' });
            return;
        }

        const wasRemovedOptionCorrect = targetQuestion.options[optionIndexToRemove].isCorrect;

        setQuestionSetFormState((previousState) => {
            const updatedQuestions = [...previousState.questions];
            let remainingOptions = updatedQuestions[questionIndex].options
                .filter((_, idx) => idx !== optionIndexToRemove)
                .map((opt, idx) => ({ ...opt, orderIndex: idx }));

            // If the deleted option was the correct one, set the first remaining option as correct
            if (wasRemovedOptionCorrect && remainingOptions.length > 0) {
                remainingOptions = remainingOptions.map((opt, idx) => ({
                    ...opt,
                    isCorrect: idx === 0
                }));
            }

            updatedQuestions[questionIndex] = {
                ...updatedQuestions[questionIndex],
                options: remainingOptions
            };

            return {
                ...previousState,
                questions: updatedQuestions
            };
        });
    };

    const handleOptionTextChange = (questionIndex, optionIndex, newText) => {
        setQuestionSetFormState((previousState) => {
            const updatedQuestions = [...previousState.questions];
            const updatedOptions = [...updatedQuestions[questionIndex].options];
            updatedOptions[optionIndex] = {
                ...updatedOptions[optionIndex],
                text: newText
            };
            updatedQuestions[questionIndex] = {
                ...updatedQuestions[questionIndex],
                options: updatedOptions
            };
            return {
                ...previousState,
                questions: updatedQuestions
            };
        });
    };

    const handleSetCorrectOption = (questionIndex, correctOptionIndex) => {
        setQuestionSetFormState((previousState) => {
            const updatedQuestions = [...previousState.questions];
            const updatedOptions = updatedQuestions[questionIndex].options.map((opt, idx) => ({
                ...opt,
                isCorrect: idx === correctOptionIndex
            }));
            updatedQuestions[questionIndex] = {
                ...updatedQuestions[questionIndex],
                options: updatedOptions
            };
            return {
                ...previousState,
                questions: updatedQuestions
            };
        });
    };

    // =========================================================================
    // SUBMIT & VALIDATION
    // =========================================================================

    const handleFormSubmit = async (formEvent) => {
        formEvent.preventDefault();

        // 1. Title validation
        if (!questionSetFormState.title || questionSetFormState.title.trim() === '') {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: 'El título del banco de preguntas es obligatorio.' });
            return;
        }

        // 2. Questions validation
        if (!Array.isArray(questionSetFormState.questions) || questionSetFormState.questions.length === 0) {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Debe agregar al menos 1 pregunta al banco.' });
            return;
        }

        for (let qIdx = 0; qIdx < questionSetFormState.questions.length; qIdx++) {
            const question = questionSetFormState.questions[qIdx];
            const questionNum = qIdx + 1;

            if (!question.instruction || question.instruction.trim() === '') {
                showMessage({
                    type: MESSAGE_TYPE.WARNING,
                    text: `La pregunta #${questionNum} no tiene texto o instrucción.`
                });
                return;
            }

            if (!Array.isArray(question.options) || question.options.length < 2) {
                showMessage({
                    type: MESSAGE_TYPE.WARNING,
                    text: `La pregunta #${questionNum} debe contener al menos 2 opciones.`
                });
                return;
            }

            let correctCount = 0;
            for (let oIdx = 0; oIdx < question.options.length; oIdx++) {
                const opt = question.options[oIdx];
                const optLetter = OPTION_LETTERS[oIdx] || `#${oIdx + 1}`;

                if (!opt.text || opt.text.trim() === '') {
                    showMessage({
                        type: MESSAGE_TYPE.WARNING,
                        text: `La opción ${optLetter} de la pregunta #${questionNum} está vacía.`
                    });
                    return;
                }

                if (opt.isCorrect) correctCount += 1;
            }

            if (correctCount !== 1) {
                showMessage({
                    type: MESSAGE_TYPE.WARNING,
                    text: `La pregunta #${questionNum} debe tener exactamente una opción correcta seleccionada.`
                });
                return;
            }
        }

        setIsSavingInProgress(true);
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Guardando banco de preguntas...' });

        try {
            const saveResponse = await SaveQuestionSet(questionSetFormState);
            if (saveResponse.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: `Banco "${questionSetFormState.title}" guardado exitosamente.`
                });
                if (onQuestionSetSavedSuccessfully) {
                    onQuestionSetSavedSuccessfully(saveResponse.data);
                }
                onCloseModal();
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: saveResponse.message || 'Error al guardar el banco de preguntas.'
                });
            }
        } catch (saveError) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error inesperado al persistir el banco de preguntas.' });
        } finally {
            setIsSavingInProgress(false);
        }
    };

    return (
        <>
            {/* Backdrop con zIndex: 99998 */}
            <div className="cModal__Background" style={{ zIndex: 99998 }} onClick={onCloseModal}></div>

            {/* Container con zIndex: 99999 */}
            <div className="cModal__Container" style={{ zIndex: 99999, maxWidth: '1050px', width: '95vw' }}>
                <div
                    className="cModal__Container__Content bg-white rounded-4 shadow-lg p-0 overflow-hidden text-dark position-relative"
                    style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
                >
                    {/* Modal Header */}
                    <div className="d-flex justify-content-between align-items-center p-3 px-4 bg-dark text-white border-bottom border-secondary">
                        <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-patch-question-fill text-warning fs-4"></i>
                            <h5 className="m-0 fw-bold">
                                {questionSetFormState.id > 0
                                    ? `Editar Banco: ${questionSetFormState.title}`
                                    : 'Crear Nuevo Banco de Preguntas - De barrio en barrio'}
                            </h5>
                        </div>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-light rounded-circle"
                            onClick={onCloseModal}
                            title="Cerrar modal"
                        >
                            <i className="bi bi-x-lg"></i>
                        </button>
                    </div>

                    {/* Scrollable Form Body */}
                    <form onSubmit={handleFormSubmit} className="d-flex flex-column flex-grow-1 overflow-hidden m-0 p-0">
                        <div className="p-4 overflow-y-auto flex-grow-1" style={{ maxHeight: 'calc(90vh - 140px)' }}>
                            {/* TOP CONFIG: TÍTULO Y DESCRIPCIÓN */}
                            <div className="row g-3 mb-4 bg-light p-3 rounded-4 border">
                                <div className="col-md-7">
                                    <label className="form-label fw-bold">
                                        <i className="bi bi-bookmark-fill text-primary me-1"></i> Título del Banco de Preguntas *
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control fw-bold"
                                        placeholder="ej. Trivia Historia ADSL, Clásico Potosino, Cultura General..."
                                        value={questionSetFormState.title}
                                        onChange={(e) => handleHeaderFieldChange('title', e.target.value)}
                                        maxLength={150}
                                        required
                                    />
                                </div>
                                <div className="col-md-5">
                                    <label className="form-label fw-bold">
                                        <i className="bi bi-card-text text-secondary me-1"></i> Descripción (Opcional)
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Descripción o temática del banco"
                                        value={questionSetFormState.description || ''}
                                        onChange={(e) => handleHeaderFieldChange('description', e.target.value)}
                                    />
                                </div>
                            </div>

                            {/* LISTA DINÁMICA DE PREGUNTAS */}
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="fw-bold text-dark m-0">
                                    <i className="bi bi-list-ol text-primary me-1"></i> Preguntas ({questionSetFormState.questions.length})
                                </h6>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-primary fw-bold px-3 shadow-sm"
                                    onClick={handleAddQuestion}
                                >
                                    <i className="bi bi-plus-lg me-1"></i> Agregar Pregunta
                                </button>
                            </div>

                            {questionSetFormState.questions.map((questionItem, qIndex) => (
                                <div
                                    key={qIndex}
                                    className="card mb-4 border shadow-sm rounded-4 overflow-hidden"
                                    style={{ borderLeft: '6px solid #0d6efd' }}
                                >
                                    {/* Question Card Header */}
                                    <div className="card-header bg-white p-3 d-flex align-items-center justify-content-between border-bottom">
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="badge bg-primary fs-6 px-3 py-1">
                                                Pregunta #{qIndex + 1}
                                            </span>
                                            {questionItem.options.length > 0 && (
                                                <span className="badge bg-light text-dark border">
                                                    {questionItem.options.length} opciones
                                                </span>
                                            )}
                                        </div>
                                        <div className="d-flex align-items-center gap-1">
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary p-1 px-2"
                                                onClick={() => handleMoveQuestion(qIndex, 'up')}
                                                disabled={qIndex === 0}
                                                title="Mover arriba"
                                            >
                                                <i className="bi bi-arrow-up"></i>
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary p-1 px-2"
                                                onClick={() => handleMoveQuestion(qIndex, 'down')}
                                                disabled={qIndex === questionSetFormState.questions.length - 1}
                                                title="Mover abajo"
                                            >
                                                <i className="bi bi-arrow-down"></i>
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-danger p-1 px-2 ms-2"
                                                onClick={() => handleRemoveQuestion(qIndex)}
                                                disabled={questionSetFormState.questions.length <= 1}
                                                title="Eliminar pregunta"
                                            >
                                                <i className="bi bi-trash-fill"></i>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Question Card Body */}
                                    <div className="card-body p-4 bg-light">
                                        {/* Instrucción o Enunciado */}
                                        <div className="mb-3">
                                            <label className="form-label fw-bold text-dark">
                                                Instrucción o Pregunta *
                                            </label>
                                            <textarea
                                                className="form-control fw-bold"
                                                rows={2}
                                                placeholder="Escribe la pregunta o reto que se proyectará a los aficionados..."
                                                value={questionItem.instruction}
                                                onChange={(e) => handleQuestionInstructionChange(qIndex, e.target.value)}
                                                required
                                            ></textarea>
                                        </div>

                                        {/* Dropzone de Imagen Opcional */}
                                        <QuestionImageDropzone
                                            currentValue={questionItem.imageUrl}
                                            questionIndex={qIndex}
                                            onUploadFile={handleQuestionImageUpload}
                                            onClearImage={handleClearQuestionImage}
                                            resolveMediaUrl={resolveMediaUrl}
                                            isUploading={uploadingQuestionIndex === qIndex}
                                        />

                                        {/* OPCIONES DE RESPUESTA */}
                                        <div className="mt-3 pt-3 border-top">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <label className="form-label fw-bold small text-muted m-0">
                                                    <i className="bi bi-ui-radios-grid me-1"></i> Opciones de Respuesta (Marca la Correcta):
                                                </label>
                                                <button
                                                    type="button"
                                                    className="btn btn-xs btn-outline-primary py-1 px-2 fw-bold"
                                                    style={{ fontSize: '0.75rem' }}
                                                    onClick={() => handleAddOptionToQuestion(qIndex)}
                                                    disabled={questionItem.options.length >= 8}
                                                >
                                                    <i className="bi bi-plus-circle me-1"></i> Agregar Opción
                                                </button>
                                            </div>

                                            <div className="row g-2">
                                                {questionItem.options.map((optionItem, oIndex) => {
                                                    const optionLetter = OPTION_LETTERS[oIndex] || `${oIndex + 1}`;
                                                    return (
                                                        <div key={oIndex} className="col-md-6">
                                                            <div
                                                                className={`p-2 rounded-3 border d-flex align-items-center gap-2 ${
                                                                    optionItem.isCorrect
                                                                        ? 'bg-success-subtle border-success'
                                                                        : 'bg-white'
                                                                }`}
                                                            >
                                                                {/* Radio button para marcar correcta */}
                                                                <div className="form-check m-0 d-flex align-items-center">
                                                                    <input
                                                                        className="form-check-input cursor-pointer"
                                                                        type="radio"
                                                                        name={`correct-option-radio-q${qIndex}`}
                                                                        id={`correct-opt-q${qIndex}-o${oIndex}`}
                                                                        checked={optionItem.isCorrect}
                                                                        onChange={() => handleSetCorrectOption(qIndex, oIndex)}
                                                                        title="Marcar como respuesta correcta"
                                                                        style={{ width: '20px', height: '20px' }}
                                                                    />
                                                                </div>

                                                                {/* Badge de letra */}
                                                                <span
                                                                    className={`badge fw-bold fs-6 ${
                                                                        optionItem.isCorrect
                                                                            ? 'bg-success text-white'
                                                                            : 'bg-secondary text-white'
                                                                    }`}
                                                                    style={{ minWidth: '32px' }}
                                                                >
                                                                    {optionLetter}
                                                                </span>

                                                                {/* Input de texto de la opción */}
                                                                <input
                                                                    type="text"
                                                                    className={`form-control form-control-sm fw-bold ${
                                                                        optionItem.isCorrect ? 'border-success text-success' : ''
                                                                    }`}
                                                                    placeholder={`Texto de la opción ${optionLetter}...`}
                                                                    value={optionItem.text}
                                                                    onChange={(e) =>
                                                                        handleOptionTextChange(qIndex, oIndex, e.target.value)
                                                                    }
                                                                    required
                                                                />

                                                                {/* Botón eliminar opción */}
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-outline-danger p-1 rounded-circle"
                                                                    onClick={() => handleRemoveOptionFromQuestion(qIndex, oIndex)}
                                                                    disabled={questionItem.options.length <= 2}
                                                                    title="Eliminar esta opción"
                                                                    style={{ width: '24px', height: '24px', lineHeight: '10px' }}
                                                                >
                                                                    <i className="bi bi-x"></i>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}

                            {/* Botón Inferior para agregar pregunta */}
                            <div className="text-center mt-2 mb-3">
                                <button
                                    type="button"
                                    className="btn btn-outline-primary fw-bold px-4 py-2 rounded-pill shadow-sm"
                                    onClick={handleAddQuestion}
                                >
                                    <i className="bi bi-plus-circle-fill me-1"></i> Agregar Otra Pregunta
                                </button>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="p-3 bg-light border-top d-flex justify-content-end gap-2">
                            <button
                                type="button"
                                className="btn btn-outline-secondary fw-bold px-4"
                                onClick={onCloseModal}
                                disabled={isSavingInProgress}
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                className="btn btn-success fw-bold px-4"
                                disabled={isSavingInProgress}
                            >
                                {isSavingInProgress ? (
                                    <>
                                        <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                                        Guardando...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-check-circle-fill me-1"></i> Guardar Banco de Preguntas
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};
